/**
 * Feedback Dialog
 * Lets users submit bug reports, suggestions, or other feedback.
 */
import React, { useState } from "react";
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
  Field,
  Textarea,
  RadioGroup,
  Radio,
  Spinner,
  makeStyles,
  tokens,
  Text,
} from "@fluentui/react-components";
import { Send24Regular, Dismiss24Regular } from "@fluentui/react-icons";
import { useTokenManager } from "../hooks/useTokenManager";
import { getConfig } from "../config";

const useStyles = makeStyles({
  form: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  successMessage: {
    color: tokens.colorPaletteGreenForeground1,
    textAlign: "center",
    padding: tokens.spacingVerticalL,
  },
  errorMessage: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: "12px",
  },
});

interface FeedbackDialogProps {
  open: boolean;
  onClose: () => void;
  version: string;
}

export const FeedbackDialog: React.FC<FeedbackDialogProps> = ({
  open,
  onClose,
  version,
}) => {
  const styles = useStyles();
  const tokenManager = useTokenManager();

  const [category, setCategory] = useState("suggestion");
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSend = async () => {
    if (!message.trim()) return;
    setIsSending(true);
    setError(null);

    try {
      let token = await tokenManager.getToken();
      if (!token) {
        token = await tokenManager.getToken(true);
      }
      if (!token) {
        throw new Error("Session expired. Please refresh the page and try again.");
      }
      const { userId, email, roles } = tokenManager.getUserInfo();
      const apiBase = getConfig().permissionApiUrl || "/api";

      const resp = await fetch(`${apiBase}/feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "X-User-Id": userId,
          "X-User-Email": email,
          "X-User-Roles": roles.join(","),
        },
        body: JSON.stringify({ category, message: message.trim(), version }),
      });

      if (!resp.ok) {
        throw new Error(`Server error: ${resp.status}`);
      }

      setSent(true);
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err: any) {
      setError(err.message || "Failed to send feedback");
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => {
    setCategory("suggestion");
    setMessage("");
    setSent(false);
    setError(null);
    setIsSending(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && handleClose()}>
      <DialogSurface>
        <DialogTitle>Send Feedback</DialogTitle>
        <DialogBody>
          <DialogContent>
            {sent ? (
              <Text className={styles.successMessage} size={400} weight="semibold">
                Thank you! Your feedback has been submitted.
              </Text>
            ) : (
              <div className={styles.form}>
                <Field label="Category">
                  <RadioGroup
                    value={category}
                    onChange={(_, data) => setCategory(data.value)}
                    layout="horizontal"
                  >
                    <Radio value="bug" label="Bug" />
                    <Radio value="suggestion" label="Suggestion" />
                    <Radio value="other" label="Other" />
                  </RadioGroup>
                </Field>
                <Field label="Your feedback">
                  <Textarea
                    value={message}
                    onChange={(_, data) => setMessage(data.value)}
                    placeholder="Describe the issue, suggestion, or feedback..."
                    rows={6}
                    resize="vertical"
                  />
                </Field>
                {error && (
                  <Text className={styles.errorMessage}>{error}</Text>
                )}
              </div>
            )}
          </DialogContent>
        </DialogBody>
        {!sent && (
          <DialogActions>
            <Button
              appearance="secondary"
              icon={<Dismiss24Regular />}
              onClick={handleClose}
              disabled={isSending}
            >
              Cancel
            </Button>
            <Button
              appearance="primary"
              icon={isSending ? <Spinner size="tiny" /> : <Send24Regular />}
              onClick={handleSend}
              disabled={isSending || !message.trim()}
            >
              {isSending ? "Sending..." : "Send"}
            </Button>
          </DialogActions>
        )}
      </DialogSurface>
    </Dialog>
  );
};
